import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { Toast, ToastClose, ToastDescription, ToastTitle } from '@/components/ui/toast';
import type { ToasterToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

export const COLLAPSED_PEEK_PX = 10;
export const COLLAPSED_MAX_OFFSET_PX = 28;
export const EXPANDED_GAP_PX = 12;
export const STACK_SCALE_STEP = 0.04;
export const DEFAULT_TOAST_HEIGHT_PX = 96;

interface StackStyleOptions {
  index: number;
  isExpanded: boolean;
  toasts: Pick<ToasterToast, 'id'>[];
  heights: Record<string, number>;
}

function getMeasuredHeight(id: string, heights: Record<string, number>) {
  return heights[id] || DEFAULT_TOAST_HEIGHT_PX;
}

export function getStackOffset({ index, isExpanded, toasts, heights }: StackStyleOptions) {
  if (!isExpanded) {
    return Math.min(COLLAPSED_MAX_OFFSET_PX, index * COLLAPSED_PEEK_PX);
  }

  return toasts.slice(0, index).reduce((offset, item) => {
    return offset + getMeasuredHeight(item.id, heights) + EXPANDED_GAP_PX;
  }, 0);
}

export function getStackStyle(options: StackStyleOptions): CSSProperties {
  const { index, isExpanded } = options;
  const offset = getStackOffset(options);
  const scale = isExpanded ? 1 : Math.max(0.88, 1 - index * STACK_SCALE_STEP);

  return {
    bottom: isExpanded ? `${offset}px` : 0,
    transform: isExpanded ? `scale(${scale})` : `translateY(-${offset}px) scale(${scale})`,
    transformOrigin: 'bottom center',
    zIndex: 50 - index,
    opacity: isExpanded || index === 0 ? 1 : Math.max(0.72, 1 - index * 0.08),
    pointerEvents: isExpanded || index === 0 ? 'auto' : 'none',
  };
}

export function getCollapsedDeckHeight(
  toasts: Pick<ToasterToast, 'id'>[],
  heights: Record<string, number>
) {
  if (toasts.length === 0) return 0;
  return getMeasuredHeight(toasts[0].id, heights) + (toasts.length - 1) * COLLAPSED_PEEK_PX;
}

function getExpandedDeckHeight(
  toasts: Pick<ToasterToast, 'id'>[],
  heights: Record<string, number>
) {
  if (toasts.length === 0) return 0;

  return toasts.reduce((height, item, index) => {
    return height + getMeasuredHeight(item.id, heights) + (index === 0 ? 0 : EXPANDED_GAP_PX);
  }, 0);
}

function isInteractiveTarget(target: EventTarget | null) {
  return target instanceof HTMLElement
    ? Boolean(target.closest('[toast-close],button,a,[role="button"],input,textarea,select'))
    : false;
}

function areHeightsEqual(next: Record<string, number>, previous: Record<string, number>) {
  const nextKeys = Object.keys(next);
  const previousKeys = Object.keys(previous);

  if (nextKeys.length !== previousKeys.length) return false;
  return nextKeys.every((key) => next[key] === previous[key]);
}

interface ToastDeckProps {
  toasts: ToasterToast[];
}

export function ToastDeck({ toasts }: ToastDeckProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const deckRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isClosingToastRef = useRef(false);
  const collapseTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const toastRefs = useRef(new Map<string, ComponentRef<typeof Toast>>());

  useLayoutEffect(() => {
    const updateHeights = () => {
      const nextHeights: Record<string, number> = {};

      toasts.forEach((item) => {
        const node = toastRefs.current.get(item.id);
        if (!node) return;

        const rectHeight = node.getBoundingClientRect().height;
        nextHeights[item.id] = Math.ceil(
          rectHeight || node.offsetHeight || DEFAULT_TOAST_HEIGHT_PX
        );
      });

      setHeights((previous) => (areHeightsEqual(nextHeights, previous) ? previous : nextHeights));
    };

    updateHeights();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateHeights);
    toasts.forEach((item) => {
      const node = toastRefs.current.get(item.id);
      if (node) observer.observe(node);
    });

    return () => {
      observer.disconnect();
    };
  }, [toasts]);

  const activeToasts = toasts.filter((item) => item.open !== false);
  const collapsedHeight = getCollapsedDeckHeight(activeToasts, heights);
  const expandedHeight = getExpandedDeckHeight(activeToasts, heights);
  const deckHeight = isExpanded ? expandedHeight : collapsedHeight;

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const rootStyle = document.documentElement.style;
    if (activeToasts.length === 0) {
      rootStyle.removeProperty('--jointo-toast-deck-height');
      return;
    }

    rootStyle.setProperty('--jointo-toast-deck-height', `${Math.ceil(collapsedHeight)}px`);

    return () => {
      rootStyle.removeProperty('--jointo-toast-deck-height');
    };
  }, [activeToasts.length, collapsedHeight]);

  useEffect(() => {
    if (!isExpanded || typeof document === 'undefined') return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (target && deckRef.current?.contains(target)) return;
      setIsExpanded(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isExpanded, expandedHeight]);

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) window.clearTimeout(collapseTimerRef.current);
    };
  }, []);

  if (toasts.length === 0) return null;

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' || isInteractiveTarget(event.target)) return;
    setIsExpanded((current) => !current);
  };

  const keepExpandedWhileClosing = () => {
    isClosingToastRef.current = true;
    setIsExpanded(true);

    window.setTimeout(() => {
      isClosingToastRef.current = false;
    }, 250);
  };

  const closeToastFromButton = (
    event: ReactPointerEvent<HTMLButtonElement>,
    onOpenChange?: (open: boolean) => void
  ) => {
    event.stopPropagation();
    keepExpandedWhileClosing();
    onOpenChange?.(false);
  };

  return (
    <div
      ref={deckRef}
      role="region"
      aria-label="通知消息"
      className="relative w-full"
      onPointerEnter={(event) => {
        if (event.pointerType !== 'mouse') return;
        if (collapseTimerRef.current) window.clearTimeout(collapseTimerRef.current);
        setIsExpanded(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse' || isClosingToastRef.current) return;

        const { clientX, clientY } = event;
        collapseTimerRef.current = window.setTimeout(() => {
          const rect = scrollRef.current?.getBoundingClientRect();
          if (
            rect &&
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom
          ) {
            return;
          }

          setIsExpanded(false);
        }, 120);
      }}
      onPointerDown={handlePointerDown}
    >
      <div
        ref={scrollRef}
        className={cn(
          'relative w-full transition-[height] duration-200 motion-reduce:transition-none',
          isExpanded ? 'max-h-[min(70vh,calc(100vh-8rem))] overflow-y-auto' : 'overflow-visible'
        )}
        style={{ height: `${Math.ceil(deckHeight)}px` }}
      >
        <div className="relative w-full" style={{ height: `${Math.ceil(deckHeight)}px` }}>
          {toasts.map(
            (
              { id, title, description, action, className, style, onOpenChange, ...props },
              index
            ) => {
              const activeIndex = activeToasts.findIndex((item) => item.id === id);
              const stackIndex =
                activeIndex >= 0
                  ? activeIndex
                  : Math.min(index, Math.max(activeToasts.length - 1, 0));
              const stackToasts = activeToasts.length > 0 ? activeToasts : [{ id }];
              const stackStyle = getStackStyle({
                index: stackIndex,
                isExpanded,
                toasts: stackToasts,
                heights,
              });

              return (
                <Toast
                  key={id}
                  ref={(node) => {
                    if (node) {
                      toastRefs.current.set(id, node);
                    } else {
                      toastRefs.current.delete(id);
                    }
                  }}
                  className={cn(
                    'absolute bottom-0 right-0 w-full origin-bottom transition-[bottom,transform,opacity] duration-200 motion-reduce:transition-none',
                    stackIndex > 0 && 'data-[state=open]:animate-none',
                    className
                  )}
                  style={{ ...style, ...stackStyle }}
                  data-toast-stack-index={stackIndex}
                  onOpenChange={onOpenChange}
                  {...props}
                >
                  <div className="grid gap-1">
                    {title && <ToastTitle>{title}</ToastTitle>}
                    {description && <ToastDescription>{description}</ToastDescription>}
                  </div>
                  {action}
                  <ToastClose
                    onPointerDownCapture={(event) => closeToastFromButton(event, onOpenChange)}
                    onClickCapture={keepExpandedWhileClosing}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenChange?.(false);
                    }}
                  />
                </Toast>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}
